import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthService, UserSession } from 'api';
import { useResponse } from 'services/hooks';
import { REVOKE_ERROR_MESSAGE, REVOKE_SUCCESS_MESSAGE, USER_SESSIONS_QUERY_KEY } from '../UserSessions.constants';

const useUserSessions = () => {
  const queryClient = useQueryClient();
  const { processErrorResponse, successNotification } = useResponse();

  const {
    data: sessions = [],
    isLoading,
    isError,
  } = useQuery<UserSession[]>({
    queryKey: USER_SESSIONS_QUERY_KEY,
    queryFn: () => AuthService.authSessionsList(),
  });

  const { mutateAsync, isPending: isRevoking } = useMutation({
    mutationFn: (sessionId: string) => AuthService.authSessionsDelete(sessionId),
    // This mutation already shows its own friendly error message below.
    // Suppress the global handler's raw message so the user doesn't see
    // both stacked.
    meta: { showGlobalError: false },
  });

  const revokeSession = useCallback(
    async (sessionId: string) => {
      try {
        await mutateAsync(sessionId);
        // Refetch rather than filtering locally: the server decides what counts
        // as active, and a revoke can race with one from another device.
        await queryClient.invalidateQueries({ queryKey: USER_SESSIONS_QUERY_KEY });
        successNotification(REVOKE_SUCCESS_MESSAGE);
        return true;
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage: REVOKE_ERROR_MESSAGE,
          action: 'sign out this session',
        });
        return false;
      }
    },
    [mutateAsync, processErrorResponse, queryClient, successNotification]
  );

  return {
    isError,
    isLoading,
    isRevoking,
    revokeSession,
    sessions,
  };
};

export default useUserSessions;
